import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  useCheckMethodsForEmail,
  useLookupUserAccount,
  useSendPasswordResetEmail,
} from '@/features/auth/queries';
import type { EnterEmailFormValues } from '@/features/auth/schema';
import { forgotPasswordSchema } from '@/features/auth/schema/enter-email-schema';
import { useUnAuthStackNavigation } from '@/hooks';
import { captureException } from '@/libs/logger';
import { UnAuthStackParamList } from '@/navigations/types';
import { useUiStore } from '@/stores';
import { yupResolver } from '@hookform/resolvers/yup';
import { RouteProp, useRoute } from '@react-navigation/native';
import { useCallback } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

export function useForgotPassword() {
  const navigation = useUnAuthStackNavigation();
  const params = useRoute<RouteProp<UnAuthStackParamList, 'ForgotPassword'>>().params;
  const initialEmail = params?.email ?? '';
  const { mutateAsync: sendResetEmail } = useSendPasswordResetEmail();
  const { mutateAsync: checkMethodsForEmail } = useCheckMethodsForEmail();
  const { mutateAsync: lookupUserAccount } = useLookupUserAccount();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EnterEmailFormValues>({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: initialEmail ?? '',
    },
  });

  const {
    field: { value: email, onChange: onChangeEmail, onBlur: onBlurEmail },
  } = useController({
    name: 'email',
    control,
  });

  const handleSendResetEmail = useCallback(
    async (data: EnterEmailFormValues) => {
      try {
        showGlobalLoading();
        const methods = await checkMethodsForEmail(data.email);
        if (methods.length > 0) {
          const userAccount = await lookupUserAccount(data.email);
          if (userAccount.emailVerified) {
            await sendResetEmail(data.email);
            showAlert({
              title: STRINGS.auth.forgotPassword.noticeTitle,
              message: STRINGS.auth.forgotPassword.successMessage,
            });
          } else {
            // TODO: Implement email verification logic
            showAlert({
              title: STRINGS.auth.enterEmail.alertTitle,
              message: STRINGS.auth.enterEmail.alertEmailNotApproved,
            });
          }
        } else {
          showAlert({
            title: STRINGS.auth.enterEmail.alertTitle,
            message: STRINGS.auth.enterEmail.alertEmailNotApproved,
          });
        }
      } catch (error) {
        captureException(
          {
            error_payload: error,
          },
          { tags: { usk_log_type: 'handle_send_reset_email' } },
        );
      } finally {
        hideGlobalLoading();
      }
    },
    [showGlobalLoading, checkMethodsForEmail, lookupUserAccount, sendResetEmail, hideGlobalLoading],
  );

  const handleFormSubmit = handleSubmit(handleSendResetEmail, invalidErrors => {
    const emailError = invalidErrors.email?.message ?? STRINGS.auth.enterEmail.errorCheckEmail;
    showAlert({
      title: STRINGS.auth.forgotPassword.alertTitle,
      message: emailError,
    });
  });

  const handleCancel = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return {
    email,
    onChangeEmail,
    onBlurEmail,
    errors,
    isSubmitting,
    handleSendResetEmail: handleFormSubmit,
    handleCancel,
  };
}
